import type { WorkflowDashboardItem } from "@/lib/api/workflow-types";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type WorkflowSummaryTileFilter =
  | { kind: 'activated' }
  | { kind: 'stuckInStatus'; minDays: number }
  | { kind: 'evalOpenLong';  minDays: number }
  | { kind: 'notActivated' }
  | { kind: 'complete' }
  | null;

/**
 * Single source of truth for whether an item matches an active WorkflowSummaryTileFilter.
 * Used by both the tile count predicates here and the filteredWorkflowItems memo in
 * behavior-insights.tsx — keeping them in lockstep and preventing predicate drift.
 */
export function matchesWorkflowSummaryFilter(
  item: WorkflowDashboardItem,
  filter: WorkflowSummaryTileFilter,
): boolean {
  if (filter === null) return true;
  switch (filter.kind) {
    case 'activated':     return item.evalActivated;
    case 'notActivated':  return !item.evalActivated;
    case 'complete':      return item.evalActivated && item.evalDone === item.evalTotal && item.evalTotal > 0;
    case 'stuckInStatus': return item.evalActivated && item.daysInEvalStatus !== null && item.daysInEvalStatus >= filter.minDays;
    case 'evalOpenLong':  return item.evalActivated && item.daysInTrack !== null && item.daysInTrack >= filter.minDays;
  }
}

interface TileDef {
  label: string;
  count: (items: WorkflowDashboardItem[]) => number;
  filter: WorkflowSummaryTileFilter;
  clickable: boolean;
}

const TILES: TileDef[] = [
  // Informational stat-callouts — not clickable
  {
    label: 'Created',
    count: (items) => items.filter(i => i.evalActivated).length,
    filter: null,
    clickable: false,
  },
  {
    label: 'Not Started',
    count: (items) => items.filter(i => i.evalActivated && i.evalDone === 0).length,
    filter: null,
    clickable: false,
  },
  // Clickable filter tiles — order: All → Stuck 14+ days → Open 30+ days → Complete → No Checklist
  {
    label: 'All',
    count: (items) => items.filter(i => matchesWorkflowSummaryFilter(i, { kind: 'activated' })).length,
    filter: { kind: 'activated' },
    clickable: true,
  },
  {
    label: 'Stuck 14+ days',
    count: (items) => items.filter(i => matchesWorkflowSummaryFilter(i, { kind: 'stuckInStatus', minDays: 14 })).length,
    filter: { kind: 'stuckInStatus', minDays: 14 },
    clickable: true,
  },
  {
    label: 'Open 30+ days',
    count: (items) => items.filter(i => matchesWorkflowSummaryFilter(i, { kind: 'evalOpenLong', minDays: 30 })).length,
    filter: { kind: 'evalOpenLong', minDays: 30 },
    clickable: true,
  },
  {
    label: 'Complete',
    count: (items) => items.filter(i => matchesWorkflowSummaryFilter(i, { kind: 'complete' })).length,
    filter: { kind: 'complete' },
    clickable: true,
  },
  {
    label: 'No Checklist',
    count: (items) => items.filter(i => matchesWorkflowSummaryFilter(i, { kind: 'notActivated' })).length,
    filter: { kind: 'notActivated' },
    clickable: true,
  },
];

function filtersMatch(
  a: WorkflowSummaryTileFilter,
  b: WorkflowSummaryTileFilter,
): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.kind !== b.kind) return false;
  if (
    (a.kind === 'stuckInStatus' && b.kind === 'stuckInStatus') ||
    (a.kind === 'evalOpenLong'  && b.kind === 'evalOpenLong')
  ) {
    return (a as { kind: string; minDays: number }).minDays ===
           (b as { kind: string; minDays: number }).minDays;
  }
  return true;
}

const TILE_FILTER_LABELS: Record<string, string> = {
  activated:     'All',
  notActivated:  'No Checklist',
  complete:      'Complete',
  stuckInStatus: 'Stuck 14+ days',
  evalOpenLong:  'Open 30+ days',
};

export function workflowSummaryFilterLabel(f: WorkflowSummaryTileFilter): string {
  if (!f) return '';
  return TILE_FILTER_LABELS[f.kind] ?? f.kind;
}

interface WorkflowEvalSummaryProps {
  items: WorkflowDashboardItem[];
  activeFilter: WorkflowSummaryTileFilter;
  onFilterChange: (filter: WorkflowSummaryTileFilter) => void;
}

export function WorkflowEvalSummary({
  items,
  activeFilter,
  onFilterChange,
}: WorkflowEvalSummaryProps) {
  return (
    <div
      className="flex flex-wrap items-center gap-2 pt-1 pb-2"
      data-testid="workflow-eval-summary"
    >
      {/* Informational group — stat callout style, no box */}
      <div className="flex items-center gap-5">
        {TILES.filter(t => !t.clickable).map((tile) => {
          const count = tile.count(items);
          const testId = `workflow-eval-tile-${tile.label.toLowerCase().replace(/\s+/g, '-')}`;
          return (
            <div key={tile.label} className="flex flex-col cursor-default" data-testid={testId}>
              <span className="text-xl font-bold tabular-nums leading-none">{count}</span>
              <span className="text-xs text-muted-foreground mt-0.5">{tile.label}</span>
            </div>
          );
        })}
      </div>

      {/* Vertical divider — visible sm+ only */}
      <div className="hidden sm:block w-px self-stretch bg-border mx-1" aria-hidden="true" />

      {/* Clickable group */}
      <div className="flex flex-wrap items-center gap-2">
        {TILES.filter(t => t.clickable).map((tile) => {
          const count = tile.count(items);
          const isActive = filtersMatch(activeFilter, tile.filter);
          const testId = `workflow-eval-tile-${tile.label.toLowerCase().replace(/\s+/g, '-')}`;
          return (
            <button
              key={tile.label}
              type="button"
              className={cn(
                "relative flex items-center gap-2 rounded-md border px-3 py-2 text-sm toggle-elevate",
                isActive
                  ? "toggle-elevated border-primary/40 bg-primary/5"
                  : "border-border bg-muted/40",
              )}
              onClick={() => onFilterChange(isActive ? null : tile.filter)}
              data-testid={testId}
            >
              <span
                className={cn(
                  isActive ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {tile.label}
              </span>
              <Badge
                variant={isActive ? "default" : "secondary"}
                className="text-xs no-default-active-elevate"
              >
                {count}
              </Badge>
            </button>
          );
        })}
      </div>
    </div>
  );
}
